import React from 'react';

import { Description, ErrorMessage, Field, Label } from '@oysterjs/ui/fieldset';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { Select } from '@oysterjs/ui/select';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { Text, TextLink } from '@oysterjs/ui/text';

import {
  DocumentPagesSource,
  type HtmlDocumentTemplateFieldsFragment,
  TemplateDesignSource,
  Visibility
} from '~/api/graphql';
import { LEGACY_DESIGN_CHOICE, PLAIN_DESIGN_CHOICE } from '~/api/data/html-templates/mutations';
import { useDocumentDesigns } from '~/components/workspaces/hooks/use-document-designs';
import { DESIGN_PATH, designEditPath } from '~/pages/settings/design-paths';

type TemplateDesign = NonNullable<HtmlDocumentTemplateFieldsFragment['design']>;
type DesignPages = { file?: { name: string } | null; printable: boolean }[];
type PageEnd = 'start' | 'end';

type ListedDesign = NonNullable<
  ReturnType<typeof useDocumentDesigns>['choices']
>['designs'][number];

export const ARCHIVED_DESIGN_NOTE =
  "Archived designs don't start new documents; they use the agency default.";
export const OWN_LOOK_NOTE = 'Keeps its saved header, footer and styles.';
export const PLAIN_LOOK_NOTE = 'Documents print plain, with no design.';
export const LEGACY_COPY_NOTE =
  "Saving keeps this template's current look in a separate Legacy copy in the Library.";
export const MISSING_TEMPLATE_PDF = 'Add a PDF or choose another option.';
/** Statics renders the editor's preview for any viewer, so it resolves public designs only. */
export const UNSHARED_DESIGN_PREVIEW_NOTE =
  "The preview shows the agency default because this design isn't shared with everyone. Documents made from this template still use it.";

const END_COPY: Record<PageEnd, { label: string; side: string; fallback: string }> = {
  start: { label: 'Start Pages', side: 'first', fallback: "The design's start pages" },
  end: { label: 'End Pages', side: 'last', fallback: "The design's end pages" }
};

/**
 * The Design select's value for a saved template: null for the agency default, PLAIN_DESIGN_CHOICE
 * for none, LEGACY_DESIGN_CHOICE for its own look, else the design's id.
 */
export function templateDesignChoice(
  template: Pick<HtmlDocumentTemplateFieldsFragment, 'designId' | 'designSource'> | undefined
): string | null {
  if (template?.designSource === TemplateDesignSource.None) return PLAIN_DESIGN_CHOICE;
  if (template?.designSource === TemplateDesignSource.Legacy) return LEGACY_DESIGN_CHOICE;
  return template?.designId ?? null;
}

/** Whether the Design select's value starts documents with no design. */
export const startsWithoutDesign = (designId: string | null) =>
  designId === PLAIN_DESIGN_CHOICE || designId === LEGACY_DESIGN_CHOICE;

/**
 * The design a template's `designId` names. Archived designs drop out of the list, but the one
 * the template already uses must stay visible, or the select would show a different design than
 * the one saved. Falls back to the template's own design while the list is still loading.
 */
export function findTemplateDesign(
  designs: ListedDesign[],
  designId: string | null,
  currentDesign: TemplateDesign | null | undefined
): ListedDesign | TemplateDesign | undefined {
  if (startsWithoutDesign(designId)) return undefined;
  if (!designId) return designs.find((design) => design.isDefault);
  return (
    designs.find((design) => design.id === designId) ??
    (currentDesign?.id === designId ? currentDesign : undefined)
  );
}

// Pages export skips (deleted files, non-PDFs) are left out so the list matches what prints.
function printableNames(pages: DesignPages | undefined): string[] {
  return (pages ?? []).flatMap((page) => (page.file && page.printable ? [page.file.name] : []));
}

function DesignPagesList(props: { end: PageEnd; names: string[] | null }) {
  const copy = END_COPY[props.end];
  if (props.names === null) {
    return <Text>{copy.fallback}</Text>;
  }
  if (props.names.length === 0) {
    return <Text>The design has no {props.end} pages.</Text>;
  }
  return (
    <ul className="list-disc pl-5 text-sm text-neutral-700 dark:text-neutral-300">
      {props.names.map((name, index) => (
        <li key={`${index}-${name}`} className="truncate" title={name}>
          {name}
        </li>
      ))}
    </ul>
  );
}

function PagesChoice(props: {
  end: PageEnd;
  value: DocumentPagesSource;
  onChange: (value: DocumentPagesSource) => void;
  designPageNames: string[] | null;
  templateUpload: React.ReactNode;
  templateFileMissing: boolean;
}) {
  const copy = END_COPY[props.end];
  return (
    <Field>
      <Label>{copy.label}</Label>
      <Description>
        What prints as the {copy.side} pages of documents made from this template.
      </Description>
      <RadioGroup
        value={props.value}
        onChange={props.onChange}
        aria-label={copy.label}
        className="space-y-2"
      >
        <RadioField>
          <Radio color="sky" value={DocumentPagesSource.Design} />
          <Label>Design&apos;s pages</Label>
        </RadioField>
        {props.value === DocumentPagesSource.Design && (
          <div className="pl-7">
            <DesignPagesList end={props.end} names={props.designPageNames} />
          </div>
        )}
        <RadioField>
          <Radio color="sky" value={DocumentPagesSource.Template} />
          <Label>This template&apos;s PDF</Label>
        </RadioField>
        {props.value === DocumentPagesSource.Template && (
          <div className="pl-7">
            {props.templateUpload}
            {props.templateFileMissing && <ErrorMessage>{MISSING_TEMPLATE_PDF}</ErrorMessage>}
          </div>
        )}
        <RadioField>
          <Radio color="sky" value={DocumentPagesSource.None} />
          <Label>None</Label>
        </RadioField>
      </RadioGroup>
    </Field>
  );
}

/**
 * The design a template starts documents on, and where each end's pages come from. Reads the
 * design list itself so the Styles editor, which never shows these fields, makes no query.
 */
export function HtmlEditorDesignFields(props: {
  /** Only a template that is Legacy now can keep its own look; no other can choose it. */
  offersLegacy: boolean;
  designId: string | null;
  currentDesign: TemplateDesign | null | undefined;
  onDesignChange: (designId: string | null) => void;
  /** The template's body prints its own header and footer, so the design's are left off. */
  printsOwnHeaderFooter: boolean;
  onPrintsOwnHeaderFooterChange: (value: boolean) => void;
  startPages: DocumentPagesSource;
  endPages: DocumentPagesSource;
  onStartPagesChange: (value: DocumentPagesSource) => void;
  onEndPagesChange: (value: DocumentPagesSource) => void;
  startUpload: React.ReactNode;
  endUpload: React.ReactNode;
  /** True when that end is set to the template's PDF but none is stored or staged. */
  startFileMissing: boolean;
  endFileMissing: boolean;
}) {
  const designs = useDocumentDesigns().choices?.designs ?? [];
  const defaultDesign = designs.find((design) => design.isDefault);
  const retainedDesign =
    props.currentDesign && !designs.some((design) => design.id === props.currentDesign?.id)
      ? props.currentDesign
      : null;
  const withoutDesign = startsWithoutDesign(props.designId);
  const selected = findTemplateDesign(designs, props.designId, props.currentDesign);
  const archivedSelected = !!selected && 'archived' in selected && selected.archived;
  const unsharedSelected =
    !!props.designId &&
    !!selected &&
    'visibility' in selected &&
    selected.visibility.level !== Visibility.Public;
  // An archived design never starts a document, so its pages are not what prints.
  const pages = (archivedSelected ? defaultDesign : selected)?.includedPages;
  const names = (end: PageEnd) =>
    pages ? printableNames(end === 'start' ? pages.before : pages.after) : null;

  return (
    <>
      <Field>
        <Label>Design</Label>
        <Description>
          Colors, fonts, headers and footers for documents made from this template.
          {!withoutDesign && (
            <>
              {' '}
              <TextLink href={selected ? designEditPath(selected.id) : DESIGN_PATH}>
                Open in Settings
              </TextLink>
            </>
          )}
        </Description>
        <Select
          value={props.designId ?? ''}
          onChange={(e) => props.onDesignChange(e.currentTarget.value || null)}
        >
          <option value="">
            {defaultDesign ? `Agency default (${defaultDesign.name})` : 'Agency default'}
          </option>
          <option value={PLAIN_DESIGN_CHOICE}>None</option>
          {props.offersLegacy && (
            <option value={LEGACY_DESIGN_CHOICE}>Legacy (template&apos;s own look)</option>
          )}
          {designs.map((design) => (
            <option key={design.id} value={design.id}>
              {design.name}
            </option>
          ))}
          {retainedDesign && (
            <option value={retainedDesign.id}>
              {retainedDesign.name}
              {retainedDesign.archived ? ' (archived)' : ''}
            </option>
          )}
        </Select>
        {props.designId === LEGACY_DESIGN_CHOICE && <Description>{OWN_LOOK_NOTE}</Description>}
        {props.designId === PLAIN_DESIGN_CHOICE && <Description>{PLAIN_LOOK_NOTE}</Description>}
        {props.offersLegacy && props.designId !== LEGACY_DESIGN_CHOICE && (
          <Description>{LEGACY_COPY_NOTE}</Description>
        )}
        {archivedSelected && <Description>{ARCHIVED_DESIGN_NOTE}</Description>}
        {!archivedSelected && unsharedSelected && (
          <Description>{UNSHARED_DESIGN_PREVIEW_NOTE}</Description>
        )}
      </Field>
      {withoutDesign ? (
        <>
          <Field>
            <Label>{END_COPY.start.label}</Label>
            {props.startUpload}
          </Field>
          <Field>
            <Label>{END_COPY.end.label}</Label>
            {props.endUpload}
          </Field>
        </>
      ) : (
        <>
          <SwitchField>
            <Label>Leave off the design&apos;s header and footer</Label>
            <Switch
              color="sky"
              checked={props.printsOwnHeaderFooter}
              onChange={props.onPrintsOwnHeaderFooterChange}
            />
          </SwitchField>
          <PagesChoice
            end="start"
            value={props.startPages}
            onChange={props.onStartPagesChange}
            designPageNames={names('start')}
            templateUpload={props.startUpload}
            templateFileMissing={props.startFileMissing}
          />
          <PagesChoice
            end="end"
            value={props.endPages}
            onChange={props.onEndPagesChange}
            designPageNames={names('end')}
            templateUpload={props.endUpload}
            templateFileMissing={props.endFileMissing}
          />
        </>
      )}
    </>
  );
}
